import type { Money } from '@/api/types';

// 收益分析以十进制字符串传输整数分，避免跨越 JavaScript 安全整数范围。
type DisplayMoney = Omit<Money, 'value'> & { value: number | string };

/**
 * 金额格式化 — 把 cTrader 风格的 Money(value + digits) 转成展示字符串。
 * 例如 { value: 1023400, digits: 2 } → "$10,234.00"
 */
export function fmtMoney(m: DisplayMoney | null | undefined, opts: { sign?: boolean; ccy?: string } = {}): string {
  if (!m || m.value == null) return '—';
  const digits = m.digits ?? 2;
  if (typeof m.value === 'string' && /^-?\d+$/.test(m.value)) {
    const raw = BigInt(m.value);
    const base = 10n ** BigInt(digits);
    const abs = raw < 0n ? -raw : raw;
    const ccy = opts.ccy ?? m.currency ?? 'USD';
    const prefix = ccy === 'USD' ? '$' : '';
    const sign = raw < 0n ? '-' : opts.sign && raw > 0n ? '+' : '';
    const decimal = digits > 0 ? `.${(abs % base).toString().padStart(digits, '0')}` : '';
    return `${sign}${prefix}${(abs / base).toLocaleString('en-US')}${decimal}`;
  }
  const num = Number(m.value) / 10 ** digits;
  const ccy = opts.ccy ?? m.currency ?? 'USD';
  const prefix = ccy === 'USD' ? '$' : '';
  const sign = opts.sign && num > 0 ? '+' : '';
  const negSign = num < 0 ? '-' : '';
  const abs = Math.abs(num);
  const formatted = abs.toLocaleString('en-US', { minimumFractionDigits: digits, maximumFractionDigits: digits });
  return `${negSign || sign}${prefix}${formatted}`;
}

export function moneyToNumber(m: DisplayMoney | null | undefined): number {
  if (!m || m.value == null) return 0;
  const digits = m.digits ?? 2;
  return Number(m.value) / 10 ** digits;
}

export function fmtNum(n: number | null | undefined, dp = 2): string {
  if (n == null || Number.isNaN(n)) return '—';
  return Number(n).toLocaleString('en-US', { minimumFractionDigits: dp, maximumFractionDigits: dp });
}

export function fmtPct(n: number | null | undefined, dp = 2, sign = false): string {
  if (n == null || Number.isNaN(n)) return '—';
  const s = sign && n >= 0 ? '+' : '';
  return `${s}${n.toFixed(dp)}%`;
}

export function fmtPnLNumber(n: number | null | undefined, dp = 2): string {
  if (n == null) return '—';
  const sign = n > 0 ? '+' : n < 0 ? '-' : '';
  return `${sign}$${Math.abs(n).toLocaleString('en-US', { minimumFractionDigits: dp, maximumFractionDigits: dp })}`;
}

export function fmtDateTime(ts: number | string | Date | null | undefined): string {
  if (ts == null) return '—';
  const d = ts instanceof Date ? ts : new Date(ts);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('zh-CN', { hour12: false });
}

export function fmtTime(ts: number | string | Date | null | undefined): string {
  if (ts == null) return '—';
  const d = ts instanceof Date ? ts : new Date(ts);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleTimeString('zh-CN', { hour12: false });
}

/** 主账户场景: 把秒数转为 "4h 23m" 这种持仓时长格式 */
export function fmtDuration(seconds: number | null | undefined): string {
  if (seconds == null || seconds <= 0) return '—';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

/** 北京时间字符串(配合顶栏时钟) */
export function nowBeijing(d: Date = new Date()): string {
  // 直接用本机时区;真正部署到非 +08 环境时再换 Intl.DateTimeFormat({ timeZone: 'Asia/Shanghai' })
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

/** 北京日期字符串(MM-DD,顶栏使用) */
export function nowBeijingDate(d: Date = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}
